import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject, filter, firstValueFrom, takeUntil } from 'rxjs';
import { UserProfileService } from './services/common/user-profile.service';
import { UserInfo } from './models/common/UserInfo';
import { CONSTANTS } from './Constants/Constants';
import { ConfirmationDialogService } from './services/common/confirmation-dialog.service';
import { NotificationMessageService } from './services/common/notification-message.service';
import { PreferenceApiService } from './services/api/preference.service';
import { SetUserPreferencesRequest } from './models/dtos/Classes/SetUserPreferencesRequest';
import { HotkeyService } from './services/common/hotkey.service';
import { TabGuardService } from './services/common/tab-guard.service';
import { BlockedViewComponent } from './blocked-view.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, BlockedViewComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  readonly title = 'Si-Quote Web';
  private readonly destroy$ = new Subject<void>();
  private isResetDialogOpen = false;
  private isResetInProgress = false;
  private unregisterResetPreferencesHotkey?: () => void;

  constructor(
    private readonly tabGuardService: TabGuardService,
    private readonly translate: TranslateService,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly userProfileService: UserProfileService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly notificationMessageService: NotificationMessageService,
    private readonly preferenceApiService: PreferenceApiService,
    private readonly hotkeyService: HotkeyService
  ) { }

  ngOnInit() {
    this.setLocaleLanguage(CONSTANTS.LANGUAGE_EN_US); // Set default language
    this.registerHotkeys();
    this.subscribeToMsalEvents();
    this.waitForMsalInitialization();
  }

  isCurrentTabBlocked(): boolean {
    return this.tabGuardService.isCurrentTabBlocked();
  }

  ngOnDestroy() {
    this.unregisterResetPreferencesHotkey?.();
    this.tabGuardService.dispose();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private registerHotkeys(): void {
    this.unregisterResetPreferencesHotkey = this.hotkeyService.register({
      id: 'reset-user-preferences',
      combo: 'ctrl+alt+r',
      preventDefault: true,
      handler: async () => {
        await this.resetUserPreferencesToDefault();
      }
    });
  }

  private async resetUserPreferencesToDefault(): Promise<void> {
    if (this.isResetDialogOpen || this.isResetInProgress) {
      return;
    }

    if (!this.userProfileService.userInfo?.gid) {
      return;
    }

    this.isResetDialogOpen = true;

    let confirmed = false;
    try {
      confirmed = await firstValueFrom(
        this.confirmationDialogService.confirmWarning(
          this.translate.instant('RESET_PREFERENCES_CONFIRM_MESSAGE'),
          this.translate.instant('RESET_PREFERENCES_TITLE')
        )
      );
    } finally {
      this.isResetDialogOpen = false;
    }

    if (!confirmed) {
      return;
    }

    try {
      this.isResetInProgress = true;
      const requestBody: SetUserPreferencesRequest = {
        userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
        preferences: null,
        firePreferences: null,
        esPreferences: null,
        proposalPreferences: null,
        collaboratorPreferences: null,
        costReviewerPreferences: null,
        taxDetailsPreferences: null,
        resetPreferencesToDefault: true
      };

      const isSuccess = await firstValueFrom(this.preferenceApiService.setPreference(requestBody));

      if (isSuccess) {
        this.notificationMessageService.showSuccess(this.translate.instant('RESET_PREFERENCES_SUCCESS_MESSAGE'));
      }
    } finally {
      this.isResetInProgress = false;
    }
  }

  private waitForMsalInitialization(): void {
    // Wait for MSAL to finish any interaction before checking for accounts
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.initializeUserInfo();
      });
  }

  private initializeUserInfo(): void {
    // Check if there's an active account
    let account = this.msalService.instance.getActiveAccount();

    // If no active account, try to get the first available account and set it as active
    if (!account) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0];
        this.msalService.instance.setActiveAccount(account);
      }
    }

    if (account) {
      this.populateUserInfo(account);
    }
  }

  private subscribeToMsalEvents(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const account = this.msalService.instance.getActiveAccount();
        if (account) {
          this.populateUserInfo(account);
        }
      });
  }

  private populateUserInfo(account: AccountInfo): void {
    const claims = account.idTokenClaims as Record<string, string>;
    if (claims) {
      const userInfo = new UserInfo();
      userInfo.sam_account_name = claims['sam_account_name'] ?? '';
      userInfo.display_name = claims['display_name'] ?? '';
      userInfo.location = claims['location'] ?? '';
      userInfo.org_code = claims['org_code'] ?? '';
      userInfo.org_id = claims['org_id'] ?? '';
      userInfo.postal_code = claims['postal_code'] ?? '';
      userInfo.mail = claims['mail'] ?? claims['preferred_username'] ?? '';
      userInfo.gid = claims['sam_account_name'] ?? claims['gid'] ?? '';
      userInfo.city = claims['city'] ?? '';
      userInfo.company_unit = claims['company_unit'] ?? '';
      userInfo.cost_center = claims['cost_center'] ?? '';
      userInfo.country = claims['country'] ?? '';
      userInfo.address_location = claims['address_location'] ?? '';
      userInfo.name = (claims['family_name'] ?? '') + ' ' + (claims['given_name'] ?? '');

      this.userProfileService.userInfo = userInfo;
    }
  }

  private setLocaleLanguage(language: string) {
    this.translate.setDefaultLang(language);
    this.translate.use(language);
  }
}
